import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RichTextEditorRef } from './rich-text-editor';
import { useApiCall } from 'redux/utils/api-hook';
import {
  HospitalizationDto,
  HospitalizationForm,
  HospitalizationFormSchema,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { hospitalizationsRequests } from 'redux/requests/hospitalizations.requests';
import { GenericDialogProps } from 'utilities/dialog-context/dialog-context-helpers';
import { Dialog, DialogContent } from '@mui/material';
import {
  DialogContentContainer,
  DialogTitleBarComponent,
} from './components/SharedDialogHookComponents';
import { FormBuilder } from '@act/storybook';
import './hospitalizations-dialog.scss';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import SubmittableDialogActions from './components/dialog-components/submittable-dialog-actions';
import { useToast } from '../app/ReusableToast';
import useUnsavedChangesDialog from './hooks/use-unsaved-changes-dialog';
import { useAppDispatch } from 'redux/store-hooks';
import { TrackingProvider } from 'react-components/tracking';
import { updateHospitalizationSuccess } from 'redux/slices/hospitalizations.slice';

export interface HospitalizationDialogInterfaceProps {
  user: UserDto;
  hospitalization: HospitalizationDto;
  callback?: () => void;
}

export type HospitalizationDialogProps = GenericDialogProps<void> &
  HospitalizationDialogInterfaceProps;

export type HospitalizationDialogCallbackInput = void;

const HospitalizationDialog = ({
  hospitalization,
  user,
  open,
  setOpen,
  callback,
}: HospitalizationDialogProps) => {
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const dispatch = useAppDispatch();
  const [
    updatedHospitalization,
    isUpdatingHospitalization,
    updateHospitalization,
  ] = useApiCall(hospitalizationsRequests.updateHospitalization);

  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isFinalSave, setIsFinalSave] = useState(false);

  // Ref to access RichTextEditor methods
  const richTextEditorRef = useRef<RichTextEditorRef>(null);

  const { control, formState, handleSubmit } = useForm<HospitalizationForm>({
    resolver: zodResolver(HospitalizationFormSchema),
    defaultValues: {
      note: hospitalization?.note ?? '<p><br></p>',
    },
  });

  const formConfig: FormConfig<HospitalizationForm> = [
    {
      type: 'input',
      name: 'note',
      label: 'Note',
      input: 'richText',
      props: {
        autoSaveFn: handleAutoSave,
        showCharacterCount: true,
        required: true,
        defaultValue: hospitalization?.note ?? '<p><br></p>',
        trackingName: 'hospitalization-note-input',
      },
    },
  ];

  // watch for final change, then save after we're done auto-saving
  useEffect(() => {
    if (!isUpdatingHospitalization) {
      if (isFinalSave && isAutoSaving) {
        void handleSave();
      }
      setIsAutoSaving(false);
    }
    if (isFinalSave && !isUpdatingHospitalization) {
      if (callback) {
        callback();
      }
    }
  }, [isAutoSaving, isUpdatingHospitalization]);

  const handleSave = async (data?: HospitalizationForm) => {
    setIsFinalSave(true);

    // Cancel any pending autosave before proceeding with manual save
    if (richTextEditorRef.current) {
      richTextEditorRef.current.cancelPendingAutoSave();
    }

    // If there is a save happening right now, delay the final save until it's done.
    if (isUpdatingHospitalization) {
      return;
    }

    const params = {
      id: hospitalization.id,
    };

    // If we cancelled an autosave, the form data might not be up to date
    // Get the current value from the RichTextEditor to ensure we have the latest
    const noteToSave = richTextEditorRef.current
      ? richTextEditorRef.current.getCurrentValue()
      : data.note;

    try {
      updateHospitalization({
        params,
        body: {
          note: noteToSave,
        },
      });

      // Mark the current value as saved to prevent unnecessary autosaves
      if (richTextEditorRef.current) {
        richTextEditorRef.current.markAsSaved(noteToSave);
      }
    } catch (err) {
      console.error(`Error updating hospitalization: ${err}`);
    }
  };

  async function handleAutoSave(autoSaveNote: string) {
    // if we're in the final save, don't try to auto-save
    if (isFinalSave) {
      return;
    }
    setIsAutoSaving(true);

    const params = {
      id: hospitalization.id,
    };
    const body = {
      note: autoSaveNote,
    };
    updateHospitalization({ params, body });
  }

  const { isDirty } = formState;
  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (updatedHospitalization && isFinalSave) {
      dispatch(updateHospitalizationSuccess(updatedHospitalization));
      toast('Hospitalization updated successfully');
      setOpen(false);
    }
  }, [updatedHospitalization]);

  return (
    <TrackingProvider contextName="hospitalization-dialog">
      <div style={{ width: '600px' }}>
        <Dialog
          open={open}
          onClose={handleClose}
          onSubmit={handleSubmit(handleSave)}
          fullWidth
        >
          <DialogContentContainer>
            <DialogTitleBarComponent
              title={`Editing Hospitalization for ${user?.firstName} ${user?.lastName}`}
              handleClose={handleClose}
            />
            <DialogContent>
              <FormBuilder
                config={formConfig}
                control={control}
                formState={formState}
              />
            </DialogContent>
          </DialogContentContainer>
          <SubmittableDialogActions
            cancelText={'Cancel'}
            submissionText={'Save'}
            isValid={formState.isValid}
            handleClose={handleClose}
            handleSubmission={handleSubmit(handleSave)}
          />
        </Dialog>
      </div>
    </TrackingProvider>
  );
};

export default HospitalizationDialog;
